import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { firestore } from "../services/firebase";
import { Artwork } from "../types/artwork";
import { 
  getStatusColor, 
  getStatusText, 
  formatDate,
  getStatusTooltip,
  getDigitizationTooltip,
  getStatusExplanation,
  getDigitizationExplanation
} from "../utils";
import Tooltip from "../components/Tooltip";
import DuplicateArtworkModal from "../components/DuplicateArtworkModal";
import BatchOperationModal from "../components/BatchOperationModal";
import "./ArtworkList.css";

const Artworks: React.FC = () => {
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [filteredArtworks, setFilteredArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterDigitized, setFilterDigitized] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("updatedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [duplicateArtwork, setDuplicateArtwork] = useState<Artwork | null>(
    null
  );

  // Batch operation states
  const [selectedArtworks, setSelectedArtworks] = useState<Set<string>>(
    new Set()
  );
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const querySnapshot = await getDocs(collection(firestore, "artworks"));
        const artworkData: Artwork[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          isDigitized: doc.data().isDigitized || false,
        })) as Artwork[];
        setArtworks(artworkData);
      } catch (error) {
        console.error("Error fetching artworks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  useEffect(() => {
    let filtered = artworks.filter((artwork) => {
      const matchesSearch =
        artwork.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artwork.artist.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artwork.institution.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artwork.location.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        filterStatus === "all" || artwork.status === filterStatus;

      const matchesDigitized =
        filterDigitized === "all" ||
        (filterDigitized === "digitized" && artwork.isDigitized) ||
        (filterDigitized === "not-digitized" && !artwork.isDigitized);

      return matchesSearch && matchesStatus && matchesDigitized;
    });

    // Sort filtered results
    filtered.sort((a, b) => {
      let valueA: any, valueB: any;

      switch (sortBy) {
        case "title":
          valueA = a.title.toLowerCase();
          valueB = b.title.toLowerCase();
          break;
        case "artist":
          valueA = a.artist.toLowerCase();
          valueB = b.artist.toLowerCase();
          break;
        case "institution":
          valueA = a.institution.toLowerCase();
          valueB = b.institution.toLowerCase();
          break;
        case "status":
          valueA = a.status;
          valueB = b.status;
          break;
        case "isDigitized":
          valueA = a.isDigitized ? 1 : 0;
          valueB = b.isDigitized ? 1 : 0;
          break;
        case "createdAt":
          valueA = new Date(a.createdAt).getTime();
          valueB = new Date(b.createdAt).getTime();
          break;
        case "updatedAt":
        default:
          valueA = new Date(a.updatedAt).getTime();
          valueB = new Date(b.updatedAt).getTime();
          break;
      }

      if (typeof valueA === "string") {
        return sortOrder === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else {
        return sortOrder === "asc" ? valueA - valueB : valueB - valueA;
      }
    });

    setFilteredArtworks(filtered);
  }, [artworks, searchTerm, filterStatus, filterDigitized, sortBy, sortOrder]);

  // Batch operation functions
  const handleSelectArtwork = (artworkId: string) => {
    const newSelected = new Set(selectedArtworks);
    if (newSelected.has(artworkId)) {
      newSelected.delete(artworkId);
    } else {
      newSelected.add(artworkId);
    }
    setSelectedArtworks(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedArtworks.size === filteredArtworks.length) {
      setSelectedArtworks(new Set());
    } else {
      setSelectedArtworks(new Set(filteredArtworks.map((a) => a.id!)));
    }
  };

  const handleToggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedArtworks(new Set());
  };

  const handleBatchOperation = () => {
    if (selectedArtworks.size > 0) {
      setShowBatchModal(true);
    }
  };

  const handleBatchOperationSuccess = async () => {
    setShowBatchModal(false);
    setSelectedArtworks(new Set());
    setIsSelectionMode(false);

    // Ricarica i dati invece di fare reload della pagina
    try {
      const querySnapshot = await getDocs(collection(firestore, "artworks"));
      const artworkData: Artwork[] = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        isDigitized: doc.data().isDigitized || false,
      })) as Artwork[];
      setArtworks(artworkData);
    } catch (error) {
      console.error("Error refreshing artworks:", error);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteDoc(doc(firestore, "artworks", id));
        setArtworks(artworks.filter((artwork) => artwork.id !== id));
      } catch (error) {
        console.error("Error deleting artwork:", error);
        alert("Error deleting artwork. Please try again.");
      }
    }
  };

  const handleDuplicateSuccess = (newArtworkId: string) => {
    const fetchArtworks = async () => {
      try {
        const querySnapshot = await getDocs(collection(firestore, "artworks"));
        const artworkData: Artwork[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          isDigitized: doc.data().isDigitized || false,
        })) as Artwork[];
        setArtworks(artworkData);
      } catch (error) {
        console.error("Error fetching artworks:", error);
      }
    };

    fetchArtworks();
    navigate(`/artwork/${newArtworkId}`);
  };

  const getStats = () => {
    return {
      total: artworks.length,
      pending: artworks.filter((a) => a.status === "pending").length,
      inProgress: artworks.filter((a) => a.status === "in-progress").length,
      obtained: artworks.filter((a) => a.status === "obtained").length,
      digitized: artworks.filter((a) => a.isDigitized).length,
      notDigitized: artworks.filter((a) => !a.isDigitized).length,
    };
  };

  // Handle stat card click to apply filter
  const handleStatCardClick = (filterType: string, value: string) => {
    if (filterType === 'status') {
      setFilterStatus(value);
      setFilterDigitized('all');
    } else if (filterType === 'digitized') {
      setFilterDigitized(value);
      setFilterStatus('all');
    } else if (filterType === 'total') {
      setFilterStatus('all');
      setFilterDigitized('all');
    }
    // Scroll to results
    setTimeout(() => {
      const artworksGrid = document.querySelector('.artworks-grid');
      if (artworksGrid) {
        artworksGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading artworks...</p>
      </div>
    );
  }

  return (
    <div className="artwork-list-container">
      {/* Header */}
      <div className="header">
        <div className="header-decoration-1"></div>
        <div className="header-decoration-2"></div>

        <div className="header-content">
          <div>
            <div className="header-title-section">
              <span style={{ fontSize: "2.5em" }}>🎨</span>
              <h1 className="header-title">Sicilian Artworks</h1>
            </div>
            <p className="header-subtitle">
              Track and manage artwork recovery across Sicily
            </p>

            {isSelectionMode && (
              <div className="selection-indicator">
                <span style={{ fontSize: "1.1em" }}>✅</span>
                <span style={{ fontSize: "1em", fontWeight: "600" }}>
                  Selection Mode: {selectedArtworks.size} artwork
                  {selectedArtworks.size !== 1 ? "s" : ""} selected
                </span>
              </div>
            )}
          </div>

          <div className="header-buttons">
            {!isSelectionMode ? (
              <>
                <button
                  onClick={handleToggleSelectionMode}
                  className="btn-batch-mode"
                >
                  <span style={{ fontSize: "1.3em" }}>☑️</span>
                  Batch Operations
                </button>
                <Link to="/add-artwork" className="btn-add-artwork">
                  <span style={{ fontSize: "1.3em" }}>➕</span>
                  Add New Artwork
                </Link>
              </>
            ) : (
              <>
                <button onClick={handleSelectAll} className="btn-select-all">
                  <span>☑️</span>
                  {selectedArtworks.size === filteredArtworks.length
                    ? "Deselect All"
                    : "Select All"}
                </button>

                <button
                  onClick={handleBatchOperation}
                  disabled={selectedArtworks.size === 0}
                  className="btn-batch-operation"
                >
                  <span>📝</span>
                  Add Operation ({selectedArtworks.size})
                </button>

                <button
                  onClick={handleToggleSelectionMode}
                  className="btn-cancel"
                >
                  <span>✕</span>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        {[
          {
            label: "Total Artworks",
            count: stats.total,
            icon: "📊",
            color: "#007bff",
            tooltip: "Click to show all artworks",
            filterType: "total",
            filterValue: "all"
          },
          {
            label: "Digitized",
            count: stats.digitized,
            icon: "🖼️",
            color: "#28a745",
            tooltip: "Click to filter digitized artworks only",
            filterType: "digitized",
            filterValue: "digitized"
          },
          {
            label: "Not Digitized",
            count: stats.notDigitized,
            icon: "📸",
            color: "#ffc107",
            tooltip: "Click to filter non-digitized artworks",
            filterType: "digitized",
            filterValue: "not-digitized"
          },
          {
            label: "Pending",
            count: stats.pending,
            icon: "📋",
            color: "#6c757d",
            tooltip: getStatusTooltip("pending") + " - Click to filter",
            filterType: "status",
            filterValue: "pending"
          },
          {
            label: "In Progress",
            count: stats.inProgress,
            icon: "⚡",
            color: "#17a2b8",
            tooltip: getStatusTooltip("in-progress") + " - Click to filter",
            filterType: "status",
            filterValue: "in-progress"
          },
          {
            label: "Obtained",
            count: stats.obtained,
            icon: "🎯",
            color: "#6f42c1",
            tooltip: getStatusTooltip("obtained") + " - Click to filter",
            filterType: "status",
            filterValue: "obtained"
          },
        ].map((stat, index) => (
          <Tooltip key={index} content={stat.tooltip} position="bottom">
            <div 
              className="stat-card stat-card-clickable"
              onClick={() => handleStatCardClick(stat.filterType, stat.filterValue)}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-count" style={{ color: stat.color }}>
                {stat.count}
              </div>
              <div className="stat-label">{stat.label}</div>
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Batch Selection Bar */}
      {isSelectionMode && (
        <div className="batch-selection-bar">
          <div className="batch-selection-content">
            <div className="batch-selection-icon">
              <span style={{ fontSize: "1.3em", color: "white" }}>☑️</span>
            </div>
            <div>
              <h3 className="batch-selection-title">Batch Operation Mode</h3>
              <p className="batch-selection-subtitle">
                Select artworks to add operations to multiple items at once
              </p>
            </div>
          </div>
          <div>
            <span className="selected-count">
              {selectedArtworks.size} selected
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-container">
        <div className="filters-decoration" />

        <div className="filters-header">
          <div className="filters-icon">
            <span style={{ fontSize: "1.5em", color: "white" }}>🔍</span>
          </div>
          <div>
            <h3 className="filters-title">Search & Filter Artworks</h3>
            <p className="filters-subtitle">
              Find exactly what you're looking for
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-bar">
            <div className="search-input-container">
              <div className="search-icon">
                <span style={{ fontSize: "1.2em", color: "white" }}>🔍</span>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, artist, institution, or location..."
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="search-clear"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="filter-pills">
          <div className="filter-grid">
            {/* Status Filter */}
            <div className="filter-card">
              <div className="filter-header">
                <div className="filter-badge status">
                  <span style={{ fontSize: "1em", color: "white" }}>🏷️</span>
                </div>
                <span className="filter-label">Project Status</span>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Statuses ({stats.total})</option>
                <option value="pending">📋 Pending ({stats.pending})</option>
                <option value="in-progress">
                  ⚡ In Progress ({stats.inProgress})
                </option>
                <option value="obtained">🎯 Obtained ({stats.obtained})</option>
              </select>
            </div>

            {/* Digitization Filter */}
            <div className="filter-card">
              <div className="filter-header">
                <div className="filter-badge digitization">
                  <span style={{ fontSize: "1em", color: "white" }}>📸</span>
                </div>
                <span className="filter-label">Digitization Status</span>
              </div>
              <select
                value={filterDigitized}
                onChange={(e) => setFilterDigitized(e.target.value)}
                className="filter-select"
              >
                <option value="all">All ({stats.total})</option>
                <option value="digitized">
                  🖼️ Digitized ({stats.digitized})
                </option>
                <option value="not-digitized">
                  📸 Not Digitized ({stats.notDigitized})
                </option>
              </select>
            </div>

            {/* Sort By */}
            <div className="filter-card">
              <div className="filter-header">
                <div className="filter-badge sort">
                  <span style={{ fontSize: "1em", color: "white" }}>📊</span>
                </div>
                <span className="filter-label">Sort Results</span>
              </div>
              <div className="sort-controls">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="filter-select sort-select"
                >
                  <option value="updatedAt">Last Updated</option>
                  <option value="createdAt">Date Created</option>
                  <option value="title">Title</option>
                  <option value="artist">Artist</option>
                  <option value="institution">Institution</option>
                  <option value="status">Status</option>
                  <option value="isDigitized">Digitization</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(e.target.value as "asc" | "desc")
                  }
                  className="filter-select sort-order"
                >
                  <option value="desc">↓ Desc</option>
                  <option value="asc">↑ Asc</option>
                </select>
              </div>
            </div>
          </div>

          {/* Active Filters Summary */}
          {(searchTerm ||
            filterStatus !== "all" ||
            filterDigitized !== "all") && (
            <div className="active-filters">
              <div className="active-filters-decoration" />

              <div className="active-filters-content">
                <div className="active-filters-info">
                  <div className="active-filters-summary">
                    <div className="active-filters-icon">
                      <span style={{ fontSize: "1.1em", color: "white" }}>
                        📊
                      </span>
                    </div>
                    <span className="active-filters-text">
                      Showing {filteredArtworks.length} of {artworks.length}{" "}
                      artworks
                    </span>
                  </div>

                  <div className="filter-tags">
                    {searchTerm && (
                      <span className="filter-tag search">
                        <span>🔍</span>"{searchTerm}"
                      </span>
                    )}
                    {filterStatus !== "all" && (
                      <span
                        className="filter-tag"
                        style={{ background: getStatusColor(filterStatus) }}
                      >
                        <span>🏷️</span>
                        {getStatusText(filterStatus)}
                      </span>
                    )}
                    {filterDigitized !== "all" && (
                      <span
                        className={`filter-tag ${
                          filterDigitized === "digitized"
                            ? "digitized"
                            : "not-digitized"
                        }`}
                      >
                        <span>
                          {filterDigitized === "digitized" ? "🖼️" : "📸"}
                        </span>
                        {filterDigitized === "digitized"
                          ? "Digitized"
                          : "Not Digitized"}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterStatus("all");
                    setFilterDigitized("all");
                  }}
                  className="btn-clear-filters"
                >
                  <span>🧹</span>
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Artworks Grid */}
      {filteredArtworks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            {artworks.length === 0 ? "🎨" : "🔍"}
          </div>
          <h3 className="empty-state-title">
            {artworks.length === 0 ? "No Artworks Yet" : "No Artworks Found"}
          </h3>
          <p className="empty-state-text">
            {artworks.length === 0
              ? "Start building your collection by adding your first artwork."
              : "Try adjusting your search terms or filters to find what you're looking for."}
          </p>
          {artworks.length === 0 ? (
            <Link to="/add-artwork" className="empty-state-action">
              ➕ Add Your First Artwork
            </Link>
          ) : (
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
                setFilterDigitized("all");
              }}
              className="empty-state-button"
            >
              🧹 Clear All Filters
            </button>
          )}
        </div>
      ) : (
        <div className="artworks-grid">
          {filteredArtworks.map((artwork) => (
            <div
              key={artwork.id}
              className={`artwork-card ${
                selectedArtworks.has(artwork.id!) ? "selected" : ""
              }`}
            >
              {/* Checkbox for selection */}
              {isSelectionMode && (
                <input
                  type="checkbox"
                  checked={selectedArtworks.has(artwork.id!)}
                  onChange={() => handleSelectArtwork(artwork.id!)}
                  className="artwork-checkbox"
                />
              )}

              {/* Status Corner */}
              <div
                className="status-corner"
                style={{ background: getStatusColor(artwork.status) }}
              >
                <span>
                  {artwork.status === "pending" && "📋"}
                  {artwork.status === "in-progress" && "⚡"}
                  {artwork.status === "obtained" && "🎯"}
                  {artwork.isDigitized && "✅"}
                </span>
                {getStatusText(artwork.status)}
              </div>

              {/* Content */}
              <div
                className={`artwork-content ${
                  isSelectionMode ? "with-checkbox" : ""
                }`}
              >
                <h3 className="artwork-title">{artwork.title}</h3>

                {/* Image Thumbnail */}
                {artwork.images && artwork.images.length > 0 && (
                  <div className="artwork-thumbnail">
                    <img 
                      src={artwork.images[0]} 
                      alt={artwork.title}
                      className="artwork-thumbnail-image"
                      loading="lazy"
                    />
                    {artwork.images.length > 1 && (
                      <div className="artwork-thumbnail-badge">
                        +{artwork.images.length - 1} more
                      </div>
                    )}
                  </div>
                )}

                <div className="artwork-details">
                  <div className="artwork-detail">
                    <span className="artwork-detail-icon">👨‍🎨</span>
                    <div>
                      <strong className="artwork-detail-label">Artist:</strong>
                      <span className="artwork-detail-value">
                        {artwork.artist}
                      </span>
                    </div>
                  </div>

                  <div className="artwork-detail">
                    <span className="artwork-detail-icon">🏛️</span>
                    <div>
                      <strong className="artwork-detail-label">
                        Institution:
                      </strong>
                      <span className="artwork-detail-value">
                        {artwork.institution}
                      </span>
                    </div>
                  </div>

                  <div className="artwork-detail">
                    <span className="artwork-detail-icon">📍</span>
                    <div>
                      <strong className="artwork-detail-label">
                        Location:
                      </strong>
                      <span className="artwork-detail-value">
                        {artwork.location}
                      </span>
                    </div>
                  </div>

                  <div className="artwork-detail">
                    <span className="artwork-detail-icon">
                      {artwork.isDigitized ? "🖼️" : "📸"}
                    </span>
                    <div>
                      <strong className="artwork-detail-label">
                        Digitization:
                      </strong>
                      <span
                        className={`artwork-detail-value ${
                          artwork.isDigitized ? "digitized" : "not-digitized"
                        }`}
                      >
                        {artwork.isDigitized
                          ? "Already digitized"
                          : "Needs photography"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="artwork-metadata">
                  {artwork.notes && (
                    <div className="artwork-notes">
                      <div className="artwork-notes-header">
                        <span style={{ fontSize: "1em" }}>💭</span>
                        <div>
                          <span className="artwork-notes-label">Notes:</span>
                          <p className="artwork-notes-text">{artwork.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions - Hidden in selection mode */}
                {!isSelectionMode && (
                  <div className="artwork-actions">
                    <div className="artwork-actions-row">
                      <Link
                        to={`/artwork/${artwork.id}`}
                        className="artwork-action view"
                      >
                        👁️ View
                      </Link>
                      <Link
                        to={`/edit-artwork/${artwork.id}`}
                        className="artwork-action edit"
                      >
                        ✏️ Edit
                      </Link>
                    </div>
                    <div className="artwork-actions-row">
                      <button
                        onClick={() => setDuplicateArtwork(artwork)}
                        className="artwork-action duplicate"
                      >
                        📋 Duplicate
                      </button>
                      <button
                        onClick={() => handleDelete(artwork.id!, artwork.title)}
                        className="artwork-action delete"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {duplicateArtwork && (
        <DuplicateArtworkModal
          artwork={duplicateArtwork}
          isOpen={!!duplicateArtwork}
          onClose={() => setDuplicateArtwork(null)}
          onSuccess={handleDuplicateSuccess}
        />
      )}

      {showBatchModal && (
        <BatchOperationModal
          selectedArtworkIds={Array.from(selectedArtworks)}
          isOpen={showBatchModal}
          onClose={() => setShowBatchModal(false)}
          onSuccess={handleBatchOperationSuccess}
        />
      )}
    </div>
  );
};

export default Artworks;
